import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, Storefront, Trophy, Crown, Check, X, CaretDown,
  MagnifyingGlass, Clock, WarningCircle, Scroll
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const STATUS_STYLES = {
  registered_venue: { color: '#6B7280', icon: Storefront },
  verified_venue: { color: '#3B82F6', icon: ShieldCheck },
  certified_competitive: { color: '#F59E0B', icon: Trophy },
  premier_partner: { color: '#9D4CDD', icon: Crown },
};

export default function AdminVerificationManager() {
  const [statuses, setStatuses] = useState([]);
  const [shops, setShops] = useState([]);
  const [allShops, setAllShops] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [tab, setTab] = useState('verified');
  const [assignModal, setAssignModal] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('verified_venue');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, shopsRes, allRes, auditRes] = await Promise.all([
        ax().get('/api/verification/venue-statuses'),
        ax().get('/api/verification/admin/shops'),
        ax().get('/api/shops'),
        ax().get('/api/verification/admin/audit-log'),
      ]);
      setStatuses(statusRes.data);
      setShops(shopsRes.data);
      setAllShops(allRes.data);
      setAuditLog(auditRes.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const assign = async () => {
    if (!assignModal) return;
    try {
      await ax().post(`/api/verification/admin/shop/${assignModal.id}/assign`, {
        status_code: selectedStatus, notes: notes || undefined,
      });
      toast.success(`Assigned: ${selectedStatus}`);
      setAssignModal(null);
      setNotes('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const revoke = async (shopId) => {
    const reason = prompt('Revocation reason:');
    if (!reason) return;
    try {
      await ax().post(`/api/verification/admin/shop/${shopId}/revoke`, { reason });
      toast.success('Status revoked');
      load();
    } catch { toast.error('Failed'); }
  };

  const verifiedShopIds = new Set(shops.map(s => s.shop_id));
  const unverifiedShops = allShops.filter(s => !verifiedShopIds.has(s.id));

  const inputCls = "w-full bg-white/[0.04] border border-[#262626] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#9D4CDD]/40 focus:outline-none placeholder:text-[#525252]";

  return (
    <div data-testid="admin-verification">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Exo 2' }}>Shop Verification</h2>
        <p className="text-[13px] text-[#525252] mt-1">Manage venue certification tiers and competitive standards</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'verified', label: 'Verified Shops', count: shops.length },
          { key: 'unverified', label: 'Unverified', count: unverifiedShops.length },
          { key: 'audit', label: 'Audit Log', count: auditLog.length },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-[#9D4CDD]/10 text-[#9D4CDD] border border-[#9D4CDD]/30' : 'text-[#525252] hover:text-white bg-[#111111] border border-[#262626]'}`}
            data-testid={`tab-${t.key}`}>
            {t.label} <span className="ml-1 text-xs opacity-60">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Status tier legend */}
      <div className="flex flex-wrap gap-2 mb-6">
        {statuses.map(s => {
          const style = STATUS_STYLES[s.status_code] || {};
          const Icon = style.icon || ShieldCheck;
          return (
            <div key={s.status_code} className="flex items-center gap-1.5 bg-[#111111] border border-[#262626] rounded-lg px-3 py-1.5">
              <Icon size={14} weight="duotone" style={{ color: style.color || '#666' }} />
              <span className="text-[11px] font-medium" style={{ color: style.color || '#666' }}>{s.public_badge_label}</span>
            </div>
          );
        })}
      </div>

      {/* Verified Shops */}
      {tab === 'verified' && (
        <div className="space-y-2">
          {loading && <div className="h-24 bg-white/[0.03] rounded-lg animate-pulse" />}
          {!loading && shops.length === 0 && <p className="text-[#525252] text-sm py-8 text-center">No verified shops yet</p>}
          {shops.map(s => {
            const style = STATUS_STYLES[s.current_status_code] || {};
            const Icon = style.icon || ShieldCheck;
            return (
              <div key={`${s.shop_id}-${s.current_status_code}`} className="bg-[#111111] border border-[#262626] rounded-lg p-4 flex items-center gap-4" data-testid={`verified-shop-${s.shop_id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${style.color || '#666'}15` }}>
                  <Icon size={20} weight="duotone" style={{ color: style.color || '#666' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{s.shop_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${style.color || '#666'}15`, color: style.color || '#666' }}>
                      {s.badge_label}
                    </span>
                    {s.expires_at && <span className="text-[10px] text-[#525252]"><Clock size={10} className="inline" /> Expires {new Date(s.expires_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setAssignModal({ id: s.shop_id, name: s.shop_name }); setSelectedStatus(s.current_status_code); }}
                    className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-[#A3A3A3] text-xs hover:text-white transition-colors">
                    Change
                  </button>
                  <button onClick={() => revoke(s.shop_id)}
                    className="px-3 py-1.5 rounded-lg bg-[#EF4444]/10 text-[#EF4444] text-xs hover:bg-[#EF4444]/20 transition-colors">
                    Revoke
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Unverified Shops */}
      {tab === 'unverified' && (
        <div className="space-y-2">
          {unverifiedShops.length === 0 && <p className="text-[#525252] text-sm py-8 text-center">All shops are verified</p>}
          {unverifiedShops.map(s => (
            <div key={s.id} className="bg-[#111111] border border-[#262626] rounded-lg p-4 flex items-center gap-4" data-testid={`unverified-shop-${s.id}`}>
              <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center">
                <Storefront size={20} className="text-[#525252]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm truncate">{s.name}</p>
                <p className="text-[10px] text-[#525252]">{s.location || 'No location'}</p>
              </div>
              <button onClick={() => { setAssignModal({ id: s.id, name: s.name }); setSelectedStatus('verified_venue'); }}
                className="px-4 py-2 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] text-xs font-medium hover:bg-[#3B82F6]/20 transition-colors"
                data-testid={`verify-btn-${s.id}`}>
                <ShieldCheck size={14} className="inline mr-1" /> Verify
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Audit Log */}
      {tab === 'audit' && (
        <div className="space-y-1.5">
          {auditLog.length === 0 && <p className="text-[#525252] text-sm py-8 text-center">No audit entries</p>}
          {auditLog.map((log, i) => (
            <div key={i} className="bg-[#111111] border border-[#262626] rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`audit-${i}`}>
              <Scroll size={14} className="text-[#525252] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs">
                  <span className="font-semibold capitalize">{log.action?.replace(/_/g, ' ')}</span>
                  {log.status_code && <span className="text-[#9D4CDD]"> → {log.status_code}</span>}
                  {log.reason && <span className="text-[#EF4444]"> ({log.reason})</span>}
                </p>
                <p className="text-[10px] text-[#525252]">Shop: {log.shop_id?.slice(-6)} | {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign Modal */}
      <AnimatePresence>
        {assignModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setAssignModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#111111] border border-[#262626] rounded-xl p-6 w-full max-w-sm" data-testid="assign-modal">
              <h3 className="text-white font-bold text-base mb-1">Assign Venue Status</h3>
              <p className="text-[#525252] text-xs mb-4">{assignModal.name}</p>
              <div className="space-y-3 mb-4">
                {statuses.filter(s => s.status_code !== 'registered_venue').map(s => {
                  const style = STATUS_STYLES[s.status_code] || {};
                  const Icon = style.icon || ShieldCheck;
                  const active = selectedStatus === s.status_code;
                  return (
                    <button key={s.status_code} onClick={() => setSelectedStatus(s.status_code)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${active ? 'border-white/[0.15] bg-white/[0.04]' : 'border-[#262626] hover:border-white/[0.1]'}`}
                      style={active ? { borderColor: `${style.color}40` } : {}}
                      data-testid={`status-option-${s.status_code}`}>
                      <Icon size={18} weight="duotone" style={{ color: style.color }} />
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{s.public_badge_label}</p>
                        <p className="text-[#525252] text-[10px]">{s.description?.slice(0, 60)}...</p>
                      </div>
                      {active && <Check size={16} style={{ color: style.color }} />}
                    </button>
                  );
                })}
              </div>
              <textarea className={inputCls} rows={2} placeholder="Admin notes (optional)"
                value={notes} onChange={e => setNotes(e.target.value)} />
              <div className="flex gap-2 mt-4">
                <button onClick={() => setAssignModal(null)} className="flex-1 py-2.5 rounded-lg bg-white/[0.04] text-[#A3A3A3] text-sm">Cancel</button>
                <button onClick={assign} className="flex-1 py-2.5 rounded-lg bg-[#9D4CDD] text-white text-sm font-semibold" data-testid="assign-confirm">Assign</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
